import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { extractTweetId, extractUsername, fetchTweetData, fetchProfileData } from "../../../services/tweetService";

// Async thunk to fetch tweet or profile data
export const fetchTweet = createAsyncThunk(
  "tweets/fetchTweet",
  async (urlOrParams, { rejectWithValue }) => {
    try {
      // Handle both old string format and new object format
      const url = typeof urlOrParams === 'string' ? urlOrParams : urlOrParams.url;
      const threadLimit = typeof urlOrParams === 'object' ? urlOrParams.threadLimit : null;
      
      // First, try to extract tweet ID
      const tweetId = extractTweetId(url);
      if (tweetId) {
        const tweetData = await fetchTweetData(tweetId, threadLimit);
        return { tweetData, tweetUrl: url, threadLimit };
      }
      
      // If not a tweet URL, try to extract username for profile
      const username = extractUsername(url);
      if (username) {
        const profileData = await fetchProfileData(username);
        return { tweetData: profileData, tweetUrl: url };
      }
      
      // Neither tweet nor profile URL
      throw new Error("Invalid Twitter URL format. Please enter a tweet or profile URL.");
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch data");
    }
  }
);

const initialState = {
  tweetData: null,
  tweetUrl: "",
  isLoading: false,
  error: null,
  previewSettings: {
    theme: "light", // 'light' | 'dark' | 'custom'
    backgroundColor: "#ffffff", // Card background color (from Theme Panel)
    cardBackgroundColor: null, // Explicit card background (overrides theme backgroundColor)
    textColor: "#0E1116",
    accentColor: "#1450FF",
    fontFamily: "Twitter",
    fontSize: 16,
    showMetrics: true,
    showThreads: true,
    showWatermark: true,
    timeDisplay: "datetime", // 'date' | 'datetime' | 'hidden'
    timeAgoDisplay: "show", // 'show' | 'hide'
    customBranding: null,
    aspectRatio: "auto", // 'auto' | '1:1' | '3:4' | '4:5' | '9:16' | '16:9'
    backgroundType: "solid", // 'solid' | 'gradient' (for wrapper background)
    wrapperBackgroundColor: "#FFFFFF", // Wrapper background color (from Background Panel)
    gradientBackground: null, // CSS gradient string or null
    paddingType: "standard", // 'compact' | 'standard' | 'spacious' | 'custom'
    customPadding: 32, // Custom padding value in pixels
    logoStyle: "twitter", // 'twitter' | 'x' | 'none'
  },
  exportFormat: "png", // 'png' | 'pdf'
  exportHistory: [],
};

const tweetsSlice = createSlice({
  name: "tweets",
  initialState,
  reducers: {
    updatePreviewSettings: (state, action) => {
      state.previewSettings = {
        ...state.previewSettings,
        ...action.payload,
      };
    },
    resetTweet: (state) => {
      state.tweetData = null;
      state.tweetUrl = "";
      state.error = null;
    },
    addToHistory: (state, action) => {
      const historyItem = {
        ...action.payload,
        timestamp: new Date().toISOString(),
      };
      state.exportHistory = [historyItem, ...state.exportHistory].slice(0, 50); // Keep last 50
      // Save to localStorage
      try {
        localStorage.setItem(
          "tweetExportHistory",
          JSON.stringify(state.exportHistory)
        );
      } catch (error) {
        console.error("Failed to save history to localStorage:", error);
      }
    },
    loadHistory: (state) => {
      try {
        const savedHistory = localStorage.getItem("tweetExportHistory");
        if (savedHistory) {
          state.exportHistory = JSON.parse(savedHistory);
        }
      } catch (error) {
        console.error("Failed to load history from localStorage:", error);
      }
    },
    setExportFormat: (state, action) => {
      state.exportFormat = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTweet.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTweet.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tweetData = action.payload.tweetData;
        state.tweetUrl = action.payload.tweetUrl;
        state.error = null;
      })
      .addCase(fetchTweet.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.tweetData = null;
      });
  },
});

export const {
  updatePreviewSettings,
  resetTweet,
  addToHistory,
  loadHistory,
  setExportFormat,
} = tweetsSlice.actions;

export default tweetsSlice.reducer;

